import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MdPerson, MdSecurity, MdTune } from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthProvider";
import { UsersApi, AuthApi } from "../../../api/common/apiIndex";
import ChangePassword from "./ChangePassword";
import EditEntityModal from "../commonUtils/EditEntityModal";
import Loader from "../commonUtils/Loader";
import Preferences from "./Preferences";

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState("profile");

  const {
    data: profile,
    isLoading: profileLoading,
  } = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => UsersApi.me(),
    initialData: user || undefined,
  });

  const [p, setP] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  useEffect(() => {
    if (profile) {
      setP({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        email: profile.email ?? "",
        phone: profile.phone ?? "",
      });
    }
  }, [profile]);

  const payloadRef = useRef(null);
  const updateProfile = useMutation({
    mutationFn: (payload) => UsersApi.updateMe(payload),
    onSuccess: (updated) => {
      const nextUser = updated ?? { ...user, ...payloadRef.current };
      updateUser(nextUser);
      queryClient.setQueryData(["me", "profile"], nextUser);
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setP({
        firstName: nextUser.firstName ?? "",
        lastName: nextUser.lastName ?? "",
        email: nextUser.email ?? "",
        phone: nextUser.phone ?? "",
      });
      toast.success("Profile updated");
      setShowEdit(false);
    },
    onError: () => toast.error("Could not update profile"),
  });

  const [initialSec, setInitialSec] = useState({ twoFactor: false });
  const [sec, setSec] = useState({ twoFactor: false });

  useEffect(() => {
    (async () => {
      try {
        const s = await AuthApi.getSecurity();
        const enabled = !!s?.twoFactor;
        setSec({ twoFactor: enabled });
        setInitialSec({ twoFactor: enabled });
      } catch { }
    })();
  }, []);

  const start2FA = useMutation({
    mutationFn: () => AuthApi.setTwoFactor({ enabled: true }),
    onSuccess: (res) => {
      setEnroll({ secretBase32: res.secretBase32, otpauthUrl: res.otpauthUrl });
      setShowVerify(true);
    },
    onError: () => {
      setSec({ twoFactor: false });
      toast.error("Could not start 2FA setup");
    },
  });

  const confirm2FA = useMutation({
    mutationFn: ({ code }) => AuthApi.enableTotp({ code }),
    onSuccess: () => {
      setShowVerify(false);
      setEnroll({ secretBase32: "", otpauthUrl: "" });
      setSec({ twoFactor: true });
      setInitialSec({ twoFactor: true });
      setCode("");
      toast.success("Two-factor enabled");
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => toast.error("Invalid code"),
  });

  const disable2FA = useMutation({
    mutationFn: () => AuthApi.setTwoFactor({ enabled: false }),
    onSuccess: () => {
      setShowVerify(false);
      setEnroll({ secretBase32: "", otpauthUrl: "" });
      setSec({ twoFactor: false });
      setInitialSec({ twoFactor: false });
      setCode("");
      toast.success("Two-factor disabled");
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => toast.error("Could not disable 2FA"),
  });

  const secDirty = sec.twoFactor !== initialSec.twoFactor;

  const [enroll, setEnroll] = useState({ secretBase32: "", otpauthUrl: "" });
  const [showVerify, setShowVerify] = useState(false);
  const [code, setCode] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    if (showEmailForm) {
      setEmailDraft(p.email ?? "");
    }
  }, [showEmailForm, p.email]);

  const changeEmail = useMutation({
    mutationFn: ({ newEmail }) => AuthApi.changeEmail({ email: newEmail }),
    onSuccess: (res, variables) => {
      const nextEmail = res?.email ?? res?.newEmail ?? variables?.newEmail ?? "";
      const normalized = String(nextEmail).trim();
      updateUser({ email: normalized, emailVerified: false });
      setP((prev) => ({ ...prev, email: normalized }));
      queryClient.invalidateQueries({ queryKey: ["me", "profile"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Email updated. Please verify the new address from your inbox.");
      setShowEmailForm(false);
      setEmailDraft("");
    },
    onError: (err) => {
      const message = err?.response?.data?.message || err?.message || "Could not change email";
      toast.error(message);
    },
  });

  const TABS = [
    { key: "profile", label: "Profile", icon: MdPerson },
    { key: "security", label: "Security", icon: MdSecurity },
    { key: "preferences", label: "Preferences", icon: MdTune },
  ];

  const [showEdit, setShowEdit] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");


  if (profileLoading && !profile) {
    return <Loader variant="page" text="Loading settings…" />;
  }

  return (
    <div className="container-fluid py-3 py-lg-4">
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body py-2">
          <div className="nav nav-pills gap-2 d-flex flex-nowrap overflow-auto p-2" role="tablist">
            {TABS.map(({ key, label, icon: Icon }) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`tab-btn rounded-pill d-flex align-items-center justify-content-between px-3 py-2 flex-shrink-0 ${tab === key ? "active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  <Icon size={18} />
                  <span className="fw-semibold">{label}</span>
                </button>
              </li>
            ))}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-3 p-md-4">
          {tab === "profile" && (
            <div className="position-relative">
              <Loader
                variant="overlay"
                size="sm"
                text={updateProfile.isPending ? "Updating…" : "Saving…"}
                show={updateProfile.isPending || changeEmail.isPending}
                backdrop={false}
              />
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <MdPerson size={20} /> Profile
                </h5>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <div className="form-text text-uppercase small">First name</div>
                  <div className="fw-semibold">{p.firstName || "—"}</div>
                </div>
                <div className="col-md-6">
                  <div className="form-text text-uppercase small">Last name</div>
                  <div className="fw-semibold">{p.lastName || "—"}</div>
                </div>
                <div className="col-md-6">
                  <div className="form-text text-uppercase small">Email</div>
                  <div className="fw-semibold">{p.email || "—"}</div>
                  <div className="text-muted small mt-1">
                    This email is your login username. Update it below and verify the new address.
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-text text-uppercase small">Phone</div>
                  <div className="fw-semibold">{p.phone || "—"}</div>
                </div>
                <div className="col-12 mt-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-pill"
                    onClick={() => setShowEdit(true)}
                    disabled={updateProfile.isPending}
                  >
                    Edit profile
                  </button>
                </div>
              </div>

              <div className="mt-4 border rounded-4 p-3 p-md-4 bg-body-tertiary">
                <h6 className="mb-2">Change email</h6>
                <p className="text-muted small mb-3">
                  We will send a verification link to the new email. You must confirm it before you can use it to log
                  in.
                </p>
                {showEmailForm ? (
                  <form
                    className="row g-3 align-items-end"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const next = String(emailDraft || "").trim();
                      console.log("email: ", next);
                      if (!next) {
                        toast.warn("Enter a new email address.");
                        return;
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
                        toast.warn("Enter a valid email address.");
                        return;
                      }
                      if (next.toLowerCase() === String(p.email || "").trim().toLowerCase()) {
                        toast.warn("That email is already on your account.");
                        return;
                      }
                      changeEmail.mutate({ newEmail: next });
                    }}
                  >
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="settings-new-email">
                        New email
                      </label>
                      <input
                        id="settings-new-email"
                        type="email"
                        className="form-control"
                        placeholder="name@example.com"
                        autoComplete="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        disabled={changeEmail.isPending}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary rounded-pill"
                        disabled={changeEmail.isPending}
                      >
                        Update email
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded-pill"
                        onClick={() => {
                          setShowEmailForm(false);
                          setEmailDraft("");
                        }}
                        disabled={changeEmail.isPending}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-pill"
                    onClick={() => setShowEmailForm(true)}
                    disabled={changeEmail.isPending}
                  >
                    Change email
                  </button>
                )}
              </div>

              <div className="mt-4">
                <ChangePassword />
              </div>
            </div>
          )}

          {tab === "security" && (
            <div className="position-relative">
              <Loader
                variant="overlay"
                size="sm"
                text="Updating security…"
                show={start2FA.isPending || disable2FA.isPending}
                backdrop={false}
              />

              <h5 className="mb-3 d-flex align-items-center gap-2">
                <MdSecurity size={20} /> Security
              </h5>

              <div className="border rounded-4 p-3 p-md-4 mb-4 bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1">Two-Factor Authentication</h6>
                    <div className="text-muted small">Add an extra layer of security.</div>
                  </div>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="twoFactor"
                      checked={sec.twoFactor}
                      onChange={(e) => setSec({ twoFactor: e.target.checked })}
                      disabled={start2FA.isPending || disable2FA.isPending}
                    />
                  </div>
                </div>
                <div className="mt-3 d-flex gap-2">
                  <button
                    className="btn btn-outline-primary rounded-pill"
                    onClick={() => {
                      if (!secDirty) return;
                      const was = !!initialSec.twoFactor;
                      const now = !!sec.twoFactor;
                      if (now && !was) { start2FA.mutate(); return; }
                      if (!now && was) { disable2FA.mutate(); return; }
                    }}
                    disabled={!secDirty || start2FA.isPending || disable2FA.isPending}
                  >
                    Update security
                  </button>
                </div>

                {showVerify && (
                  <div className="mt-4 p-3 p-md-4 border rounded-4 bg-body position-relative">
                    <Loader variant="overlay" size="sm" text="Verifying…" show={confirm2FA.isPending} backdrop={true} />
                    <div className="row g-3 align-items-start">
                      <div className="col-md-6">
                        <div className="fw-semibold mb-2">Scan this QR in your Authenticator</div>
                        <div className="border rounded-3 p-2 d-inline-block bg-body">
                          <img
                            alt="TOTP QR"
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                              enroll.otpauthUrl || ""
                            )}`}
                            width={220}
                            height={220}
                            loading="lazy"
                          />
                        </div>
                        <div className="small text-muted mt-2">
                          Or enter this key manually: <span className="fw-semibold">{enroll.secretBase32}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Enter 6-digit code</label>
                        <input
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          className="form-control"
                          placeholder="123456"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                          disabled={confirm2FA.isPending}
                        />
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-primary rounded-pill"
                            disabled={confirm2FA.isPending || code.length !== 6}
                            onClick={() => confirm2FA.mutate({ code })}
                          >
                            Confirm
                          </button>
                          <button
                            className="btn btn-outline-secondary rounded-pill"
                            disabled={disable2FA.isPending || confirm2FA.isPending}
                            onClick={() => {
                              setCode("");
                              disable2FA.mutate();
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {tab === "preferences" && <Preferences />}
        </div>
      </div>

      {showEdit && (
        <EditEntityModal
          title="Edit profile"
          row={{
            firstName: p.firstName,
            lastName: p.lastName,
            country: profile?.country ?? "Sweden",
            phone: p.phone,
            email: p.email,
          }}
          includeEmail={false}
          isBusy={updateProfile.isPending}
          onClose={() => setShowEdit(false)}
          onSubmit={(payload) => {
            payloadRef.current = payload;
            updateProfile.mutate(payload);
          }}
        />
      )}
    </div>
  );
}
